import type { BrowserOptions } from '@sentry/browser'
import * as Sentry from '@sentry/react'
import axios, { type AxiosError } from 'axios'
import config from 'config'
import { useEffect } from 'react'
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom'
import { getAxiosNetworkFingerprint, getAxiosNetworkFingerprintString, getMappedAxiosError } from '../api/axios/helpers'
import { IGNORED_SENTRY_ERRORS } from '../tiger/Constants'

// @sentry/types was unpublished from v8 onwards. Extras was only ever
// Record<string, unknown>, so it is declared locally rather than reached for
// through a package internal path.
type Extras = Record<string, unknown>

const dsn = process.env.VITE_REACT_APP_SENTRY_DSN

// Injected at build time by vite.config.ts. Must match the release the source
// maps were uploaded under, or events arrive outside it.
const release = process.env.VITE_SENTRY_RELEASE

export const captureException = (err: Error | AxiosError, extra?: Extras) => {
  if (!config.enableSentry) {
    return
  }

  if (axios.isAxiosError(err)) {
    return captureAxiosError(err, extra)
  }

  Sentry.captureException(err, { extra })
}

export const captureAxiosError = (err: AxiosError, extra?: Extras) => {
  if (!config.enableSentry) {
    return
  }

  Sentry.captureException(getMappedAxiosError(err), (scope) => {
    scope.setFingerprint(getAxiosNetworkFingerprint(err))
    scope.setTag('transaction', getAxiosNetworkFingerprintString(err))
    extra && scope.setExtras(extra)
    return scope
  })
}

export function SentryInit() {
  useEffect(() => {
    // v10 replaces the BrowserTracing class and the third-party
    // react-router-v6-instrumentation package with a built-in integration that
    // takes the router hooks directly. This component renders inside
    // BrowserRouter, which is what those hooks require.
    const browserTracing = Sentry.reactRouterBrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    })

    let sentryOptions: BrowserOptions = {
      dsn: dsn,
      release: release,
      integrations: [browserTracing],
      environment: config.name,
      ignoreErrors: IGNORED_SENTRY_ERRORS,
      enabled: config.enableSentry,
    }

    // Sometimes we don't want extended tracing and replays, for example when running the project locally
    if (config.enableSentryTracing) {
      const replay = Sentry.replayIntegration({ maskAllText: true })

      sentryOptions = {
        ...sentryOptions,
        integrations: [browserTracing, replay],
        tracesSampleRate: 1.0,
        replaysOnErrorSampleRate: 1.0,
      }
    }

    Sentry.init(sentryOptions)
  }, [])

  return null
}
